import { z } from 'zod/v4';

/**
 * Translation keys of the payment form's publish checks — the issue messages of
 * {@link paymentFormLegacyDataRulesSchema}.
 */
export const PAYMENT_FORM_WIDGET_RULE_MESSAGES = [
    'payment_form_invalid',
    'address_book_required',
    'pipeline_required',
    'deal_name_required',
    'variable_required',
] as const;

export type PaymentFormWidgetRuleMessage = (typeof PAYMENT_FORM_WIDGET_RULE_MESSAGES)[number];

/** One failed check: the data path it belongs to and its translation key. */
export type PaymentFormWidgetRuleIssue = {
    path: (string | number)[];
    message: PaymentFormWidgetRuleMessage;
};

/** The part of the payment-form data the checks read. */
export type PaymentFormWidgetRulesData = {
    paymentFormName?: string | null;
    productName?: string | null;
    productType?: string | null;
    productSettings?: { courseId?: unknown } | null;
    paymentElements?: ReadonlyArray<{ amount?: unknown }> | null;
    createAddressBook?: boolean | null;
    selectedAddressBook?: unknown;
    selectedPipeline?: unknown;
    dealName?: string | null;
    elements?: ReadonlyArray<{
        type: string;
        crmContactVariable?: unknown;
        crmDealVariable?: unknown;
    }> | null;
};

const isEmpty = (value: unknown): boolean => value === null || value === undefined || value === '';

/**
 * What a payment form still lacks before it can be published. Not part of the
 * data schema: an unfinished form is a valid, saveable state.
 */
export function getPaymentFormWidgetRuleIssues(data: PaymentFormWidgetRulesData): PaymentFormWidgetRuleIssue[] {
    const issues: PaymentFormWidgetRuleIssue[] = [];

    const isProductTypeDefault = data.productType === 'default';
    const isCourseMissing = data.productType === 'eduCourse' && !data.productSettings?.courseId;

    // One message for the whole product block, as the panel shows it — on the data itself.
    if (
        isEmpty(data.paymentFormName) ||
        isEmpty(data.productName) ||
        isCourseMissing ||
        isEmpty(data.paymentElements?.[0]?.amount)
    ) {
        issues.push({ path: [], message: 'payment_form_invalid' });
    }

    if (data.createAddressBook && isEmpty(data.selectedAddressBook)) {
        issues.push({ path: ['selectedAddressBook'], message: 'address_book_required' });
    }

    if (isProductTypeDefault && isEmpty(data.selectedPipeline)) {
        issues.push({ path: ['selectedPipeline'], message: 'pipeline_required' });
    }

    if (isProductTypeDefault && isEmpty(data.dealName)) {
        issues.push({ path: ['dealName'], message: 'deal_name_required' });
    }

    (data.elements ?? []).forEach((element, index) => {
        if (element.type === 'email' || element.type === 'phone') {
            return;
        }

        if (isEmpty(element.crmContactVariable) && isEmpty(element.crmDealVariable)) {
            issues.push({ path: ['elements', index], message: 'variable_required' });
        }
    });

    return issues;
}

/**
 * {@link getPaymentFormWidgetRuleIssues} as a schema over the payment-form
 * `data`, for `validateStandardSchema` — each issue lands on the field it names.
 */
export const paymentFormLegacyDataRulesSchema = z
    .custom<PaymentFormWidgetRulesData>((value) => typeof value === 'object' && value !== null)
    .superRefine((data, ctx) => {
        getPaymentFormWidgetRuleIssues(data).forEach(({ path, message }) =>
            ctx.addIssue({ code: 'custom', path, message })
        );
    });
